import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const FilterBar = ({
  filters,
  onFilterChange,
  onSearch,
  onRegisterCard,
  searchQuery,
  onSearchChange
}) => {
  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 0, label: 'Active' },
    { value: 1, label: 'Restricted' },
    { value: 2, label: 'Inactive' }
  ];

  const userTypeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'individual', label: 'Individual' },
    { value: 'team', label: 'Team' }
  ];

  const dateRangeOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'quarter', label: 'This Quarter' },
    { value: 'year', label: 'This Year' }
  ];

  return (
    <div className="bg-surface border border-border rounded-lg p-6 mb-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        {/* Filter Controls */}
        <div className="flex flex-col sm:flex-row gap-4 flex-1">
          {/* Status Filter */}
          <div className="min-w-0 flex-1 sm:max-w-48">
            <label className="block text-sm font-medium text-text-primary mb-2">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => onFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* User Type Filter */}
          <div className="min-w-0 flex-1 sm:max-w-48">
            <label className="block text-sm font-medium text-text-primary mb-2">
              User Type
            </label>
            <select
              value={filters.userType}
              onChange={(e) => onFilterChange('userType', e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
            >
              {userTypeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Date Range Filter */}
          <div className="min-w-0 flex-1 sm:max-w-48">
            <label className="block text-sm font-medium text-text-primary mb-2">
              Date Range
            </label>
            <select
              value={filters.dateRange}
              onChange={(e) => onFilterChange('dateRange', e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
            >
              {dateRangeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Search Input */}
          <div className="min-w-0 flex-1 sm:max-w-80">
            <label className="block text-sm font-medium text-text-primary mb-2">
              Search
            </label>
            <div className="relative">
              <Icon
                name="Search"
                size={18}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted"
              />
              <Input
                type="search"
                placeholder="Search by Card ID or User Name..."
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                className="pl-10 pr-4"
              />
              {searchQuery && (
                <button
                  onClick={() => onSearchChange('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-muted hover:text-text-primary transition-colors duration-150"
                >
                  <Icon name="X" size={16} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Register New Card Button */}
        <div className="flex-shrink-0">
          <Button
            variant="primary"
            onClick={onRegisterCard}
            iconName="Plus"
            iconPosition="left"
            className="w-full sm:w-auto"
          >
            Register New Card
          </Button>
        </div>
      </div>

      {/* Active Filters Display */}
      {(filters.status !== 'all' || filters.userType !== 'all' || filters.dateRange !== 'all' || searchQuery) && (
        <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-border">
          <span className="text-sm text-text-muted">Active filters:</span>

          {filters.status !== 'all' && (
            <span className="inline-flex items-center px-2 py-1 bg-accent/20 text-accent text-xs font-medium rounded-full">
              Status: {statusOptions.find(opt => opt.value === filters.status)?.label}
              <button
                onClick={() => onFilterChange('status', 'all')}
                className="ml-1 hover:text-accent-foreground"
              >
                <Icon name="X" size={12} />
              </button>
            </span>
          )}

          {filters.userType !== 'all' && (
            <span className="inline-flex items-center px-2 py-1 bg-accent/20 text-accent text-xs font-medium rounded-full">
              Type: {userTypeOptions.find(opt => opt.value === filters.userType)?.label}
              <button
                onClick={() => onFilterChange('userType', 'all')}
                className="ml-1 hover:text-accent-foreground"
              >
                <Icon name="X" size={12} />
              </button>
            </span>
          )}

          {filters.dateRange !== 'all' && (
            <span className="inline-flex items-center px-2 py-1 bg-accent/20 text-accent text-xs font-medium rounded-full">
              Date: {dateRangeOptions.find(opt => opt.value === filters.dateRange)?.label}
              <button
                onClick={() => onFilterChange('dateRange', 'all')}
                className="ml-1 hover:text-accent-foreground"
              >
                <Icon name="X" size={12} />
              </button>
            </span>
          )}

          {searchQuery && (
            <span className="inline-flex items-center px-2 py-1 bg-accent/20 text-accent text-xs font-medium rounded-full">
              Search: "{searchQuery}"
              <button
                onClick={() => onSearchChange('')}
                className="ml-1 hover:text-accent-foreground"
              >
                <Icon name="X" size={12} />
              </button>
            </span>
          )}

          <Button
            variant="ghost"
            onClick={() => {
              onFilterChange('status', 'all');
              onFilterChange('userType', 'all');
              onFilterChange('dateRange', 'all');
              onSearchChange('');
            }}
            className="text-xs px-2 py-1 h-auto"
          >
            Clear All
          </Button>
        </div>
      )}
    </div>
  );
};

export default FilterBar;